import React, { useState } from "react";
import $ from "jquery";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTheme } from "@mui/material/styles";
import { Grid, Select, Typography, Stack } from "@mui/material";

import "../../../vendor/react-minimal-datetime-range.css";

import DetectionMap from "../../../components/pages/llm/guard/dashboard/V_DetectionWorld";
import DetectChart from "../../../components/pages/llm/guard/dashboard/C_Detection";
import Data from "../../../components/pages/llm/guard/dashboard/L_Data";
import FilterResultModal from "../../../components/pages/llm/guard/dashboard/M_Filter";

import useAuth from "../../../hooks/useAuth";
import useZone from "../../../hooks/user/useZone";
import { formatDateOnly, formatTimeOnly } from "../../../utils/format";

import { MenuItem, SnackbarAlert } from "../../../components/pages/common/styled";
import { ZONE_ID_ALL } from "../../../utils/constants";
import TimeRangePicker from "../../../components/common/TimeRangePicker";

function LLMDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { configZone } = useParams();
  const {
    isAuthenticated,
    homeController,
    llmdashController,
    websiteController,
    zoneController,
    llmeventController,
    planController,
    setLLMDashController,
  } = useAuth();
  const {
    zoneList,
    rows_per_page,
    top_region_detection,
    top_source_detection,
    top_path,
    top_ua,
    top_prompt_scanners,
    top_output_scanners,
    getDashboardInfo,
    getZonesForItems,
    getOnlyEvents,
    setTotalCountToZero,
    errMsg,
    setErr,
  } = useZone();

  const zoneUid = configZone;

  const [zoneID, setZoneID] = useState();

  const [timeRange, setTimeRange] = React.useState("7d");
  const [currentDate, setCurrentDate] = React.useState(formatDateOnly(new Date()));
  const [currentTime, setCurrentTime] = React.useState(formatTimeOnly(new Date()));
  const [customRangeConfirm, setCustomRangeConfirm] = React.useState(false);
  const [filter, setFilter] = React.useState([
    {
      key: null,
      value: null,
      condition: "eq",
    },
  ]);
  const [filterOpen, setFilterOpen] = React.useState(false);

  const filterClose = () => {
    setTotalCountToZero();
    setFilterOpen(false);
  };

  const now = new Date();
  const oneDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const date = String(now.getDate()).padStart(2, "0");
  const year = String(now.getFullYear());
  const _month = String(oneDayAgo.getMonth() + 1).padStart(2, "0");
  const _date = String(oneDayAgo.getDate()).padStart(2, "0");
  const _year = String(oneDayAgo.getFullYear());
  const timeZone =
    now.getTimezoneOffset() >= 0
      ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
      : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
  const [customDateRange, setCustomDateRange] = React.useState([
    _year + "-" + _month + "-" + _date + "T00:00:00",
    year + "-" + month + "-" + date + "T00:00:00",
  ]);
  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getZonesForItems();
    }
    return () => setErr(null);
  }, [isAuthenticated, getZonesForItems, setErr]);
  React.useEffect(() => {
    if (zoneUid) {
      if (ZONE_ID_ALL === zoneUid) {
        setZoneID(ZONE_ID_ALL);
      } else {
        if (0 < zoneList?.length) {
          const zone = zoneList.find((s) => s.id === zoneUid);
          if (zone) {
            setZoneID(zone.zone_id);
          }
        }
      }
    }
  }, [zoneList, zoneUid]);
  React.useEffect(() => {
    if (filter[0].key && filter[0].value) {
      if (zoneID) {
        getOnlyEvents(zoneID, getTimeRange(), rows_per_page, 0, filter);
      }
      setFilterOpen(true);
    }
  }, [filter, getOnlyEvents]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(formatDateOnly(new Date()));
      setCurrentTime(formatTimeOnly(new Date()));
    }, 500);
    return () => clearInterval(interval);
  });
  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (llmdashController) llmdashController.abort();
      if (websiteController) websiteController.abort();
      if (zoneController) zoneController.abort();
      if (llmeventController) llmeventController.abort();
      if (planController) planController.abort();
    }
    return () => setErr(null);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
    if (homeController) homeController.abort();
    if (llmdashController) llmdashController.abort();
    if (websiteController) websiteController.abort();
    if (llmeventController) llmeventController.abort();
    if (planController) planController.abort();
    if (zoneID) {
      getDashboardInfo(setLLMDashController, zoneID, getTimeRange());
    }
  }, [isAuthenticated, zoneID, timeRange, customDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  const getTimeRange = () => {
    if (timeRange === "custom") {
      return {
        time_zone: timeZone,
        from: customDateRange[0],
        to: customDateRange[1],
      };
    } else {
      return { period: timeRange };
    }
  };
  const selectCurrentZone = (event) => {
    navigate(`/llm/${event.target.value}/guard/dashboard`);
  };
  const selectPeriod = (period) => {
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(res);
  };

  return (
    <React.Fragment>
      <Helmet title="LLM - Dashboard" />

      <Grid container spacing={6} mt={"14px"}>
        <Grid item>
          <Typography variant="h1" gutterBottom display="inline">
            LLM Dashboard
          </Typography>
        </Grid>
        <Grid item xs></Grid>
        <Grid item textAlign="center">
          <Typography variant="h2" color={theme.palette.custom.blue.main}>
            {currentTime}
          </Typography>
          <Typography variant="textTiny" color={theme.palette.custom.blue.main}>
            {currentDate.replace(", ", " / ")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={12} py={4}>
        <Grid item xs={12} md={6}>
          <Grid container>
            <Grid item xs={12} pb={2}>
              <Typography variant="h2">Choose Zones</Typography>
            </Grid>
            <Grid item xs={12}>
              <Select value={zoneUid} onChange={selectCurrentZone} sx={{ width: "100%", border: "none" }}>
                <MenuItem key="-1" value={ZONE_ID_ALL}>
                  All Zones
                </MenuItem>
                {zoneList?.map((zone, i) => {
                  return (
                    <MenuItem key={i} value={zone.id}>
                      {zone.zone_id}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container>
            <Grid item xs={12} pb={2}>
              <Typography variant="h2">Time Range</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <TimeRangePicker
                selectPeriod={selectPeriod}
                selectCustomDateRange={selectCustomDateRange}
                timeRange={timeRange}
                customDateRange={customDateRange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={6} pt={6}>
        <Grid item xs={12} md={6}>
          <Stack direction="column" spacing={5}>
            <Typography variant="h2" display="inline">
              LLM Detections by Region
            </Typography>
            <DetectionMap />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="column" spacing={5}>
            <Typography variant="h2" display="inline">
              LLM Detections Statistics
            </Typography>
            <DetectChart
              setCustomRangeConfirm={setCustomRangeConfirm}
              setTimeRange={setTimeRange}
              setCustomDateRange={setCustomDateRange}
            />
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={6} mt={6}>
        <Grid item xs={12} md={4}>
          <Data title={"Top LLM Detections By Country"} data={top_region_detection} type={"country_iso_code"} setFilter={setFilter} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Data title={"Top User Agent"} data={top_ua} type={"ua"} setFilter={setFilter} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Data title={"Top Paths"} data={top_path} type={"path"} setFilter={setFilter} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Data title={"Top Events By Source"} data={top_source_detection} type={"addr"} setFilter={setFilter} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Data title={"Top Prompt Scanners"} data={top_prompt_scanners} type={"prompt_scanner"} setFilter={setFilter} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Data title={"Top Output Scanners"} data={top_output_scanners} type={"output_scanner"} setFilter={setFilter} />
        </Grid>
      </Grid>

      <FilterResultModal open={filterOpen} handleClose={filterClose} zoneID={zoneID} timeRange={getTimeRange()} />
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMDashboard;
